import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ApiError, UnauthorizedError } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import { useCounts } from '../../hooks/useCounts'
import { useToast } from '../../hooks/useToast'
import { exportCsv } from '../../lib/csv'
import { formatDate, hasRealDeadline, sortOpps, urgencyRank } from '../../lib/format'
import { generateReport } from '../../lib/report'
import type { Opportunity } from '../../lib/types'

/**
 * The dashboard — the prototype's renderDashboard/renderOpps/oppCard as one view.
 *
 * What changed from the prototype: data comes from GET /api/opportunities and GET
 * /api/pipeline instead of localStorage, cards are keyed by their database uuid instead
 * of a base64 hash of title+buyer, and Pursue POSTs to the shared pipeline so the whole
 * team sees it. Filtering and sorting stay client-side — a few dozen rows don't need
 * server round-trips, and it keeps the stat-card clicks instant.
 */

type Filter = 'all' | 'hot' | 'warm' | 'urgent'

function OppCard({
  opp,
  open,
  inPipeline,
  canPursue,
  canDelete,
  onToggle,
  onPursue,
  onDelete,
}: {
  opp: Opportunity
  open: boolean
  inPipeline: boolean
  canPursue: boolean
  canDelete: boolean
  onToggle: () => void
  onPursue: () => void
  onDelete: () => void
}) {
  const band = opp.band?.toLowerCase() || 'watch'

  const deadlineTag = hasRealDeadline(opp) ? (
    <span className={`opp-deadline ${opp.deadlineUrgency || 'watch'}`}>
      {opp.deadlineUrgency === 'urgent' ? '⚡ ' : opp.deadlineUrgency === 'soon' ? '⏰ ' : ''}
      {opp.deadline}
    </span>
  ) : (
    <span className="opp-deadline watch">PIN</span>
  )

  return (
    <div className={`opp-card ${band}${open ? ' open' : ''}`}>
      <div className="opp-summary" onClick={onToggle}>
        <div className="opp-score">{opp.fitScore ?? '-'}</div>
        <span className={`opp-band ${band}`}>{opp.band || 'WATCH'}</span>
        <div className="opp-main">
          <div className="opp-title">{opp.title}</div>
          <div className="opp-buyer">
            {opp.buyer} · {opp.noticeType || 'Tender'}
          </div>
        </div>
        <div className="opp-meta">
          {opp.value && opp.value !== 'TBC' && <span className="opp-value">{opp.value}</span>}
          {deadlineTag}
        </div>
        <span className="opp-chevron">▶</span>
      </div>
      <div className="opp-detail">
        <div className="opp-why">{opp.why}</div>
        {opp.capabilities.length > 0 && (
          <div className="opp-tags">
            {opp.capabilities.map((c) => (
              <span className="opp-tag" key={c}>
                {c}
              </span>
            ))}
          </div>
        )}
        {opp.action && opp.actionDetail && (
          <div className="opp-action-box">
            <div className="opp-action-label">{opp.action}</div>
            <div className="opp-action-text">{opp.actionDetail}</div>
          </div>
        )}
        {opp.risks && (
          <div className="opp-risks">
            <strong>Risks:</strong> {opp.risks}
          </div>
        )}
        <div className="opp-btns">
          {/* Pursue writes to the shared pipeline — Researcher only, like every write. */}
          {canPursue && (
            <button className="btn-pursue" onClick={onPursue} disabled={inPipeline}>
              {inPipeline ? '✓ In pipeline' : '🚀 Pursue'}
            </button>
          )}
          {opp.url ? (
            <a href={opp.url} target="_blank" rel="noopener noreferrer" className="btn-outline">
              🔗 View notice
            </a>
          ) : (
            <a
              href={`https://www.find-tender.service.gov.uk/Search/Results?keywords=${encodeURIComponent(opp.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
            >
              🔍 Find on FaTS
            </a>
          )}
          {/* Curation, not workflow: scans and the prototype's seed data inevitably carry
              stale notices (closed bids, old awards). Admin-only, like every destructive
              action, and the server enforces it regardless of what this flag says. */}
          {canDelete && (
            <button className="btn-outline btn-delete" onClick={onDelete}>
              🗑 Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function DashboardView() {
  const { can } = useAuth()
  const { refresh: refreshCounts } = useCounts()
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const [opps, setOpps] = useState<Opportunity[]>([])
  const [pipelineOppIds, setPipelineOppIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Scan history rows deep-link here with ?filter=hot; anything unrecognised means 'all'.
  const [filter, setFilter] = useState<Filter>(() => {
    const f = params.get('filter')
    return f === 'hot' || f === 'warm' || f === 'urgent' ? f : 'all'
  })
  const [openCards, setOpenCards] = useState<Set<string>>(new Set())
  // ?scan=<id> shows exactly what one scan saved — the history's "View N" contract.
  const scanParam = params.get('scan')
  // The history row's yield hint; used only when the scan predates per-scan links.
  const bandParam = params.get('band')
  const legacyBand: Filter =
    bandParam === 'hot' || bandParam === 'warm' || bandParam === 'urgent' ? bandParam : 'all'

  useEffect(() => {
    Promise.all([api.opportunities(), api.pipeline()])
      .then(([o, p]) => {
        setOpps(o)
        setPipelineOppIds(new Set(p.map((item) => item.opportunityId)))
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load opportunities.'))
      .finally(() => setLoading(false))
  }, [])

  const hot = useMemo(() => opps.filter((n) => n.band === 'HOT'), [opps])
  const warm = useMemo(() => opps.filter((n) => n.band === 'WARM'), [opps])
  const urgent = useMemo(() => opps.filter((n) => urgencyRank(n.deadlineUrgency) === 0), [opps])

  // Rows saved by the linked scan. Empty for scans that predate per-scan links (the seed
  // data and early scans have scanId null) — those fall back to the full board, with a
  // note saying so, rather than a baffling empty list.
  const scanOpps = useMemo(
    () => (scanParam ? opps.filter((o) => o.scanId === scanParam) : []),
    [opps, scanParam],
  )
  const scanMode = !!scanParam && !loading
  const legacyScan = scanMode && scanOpps.length === 0

  const filtered = useMemo(() => {
    if (scanMode && !legacyScan) return sortOpps(scanOpps)
    // Legacy scan mode filters by the band the scan yielded; otherwise the user's filter.
    const f = legacyScan ? legacyBand : filter
    const subset = f === 'hot' ? hot : f === 'warm' ? warm : f === 'urgent' ? urgent : opps
    return sortOpps(subset)
  }, [opps, hot, warm, urgent, filter, scanMode, legacyScan, legacyBand, scanOpps])

  const top10 = filtered.slice(0, 10)
  const rest = filtered.slice(10)

  /** Leave scan mode; keep whatever band filter the user picked. */
  function clearScan(next: Filter = filter) {
    setFilter(next)
    setParams(next === 'all' ? {} : { filter: next })
  }

  const lastUpdated = useMemo(
    () => opps.map((o) => o.savedAt).sort().at(-1) ?? null,
    [opps],
  )

  function toggleCard(id: string) {
    setOpenCards((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function pursue(opp: Opportunity) {
    try {
      await api.pursue(opp.id)
      setPipelineOppIds((prev) => new Set(prev).add(opp.id))
      toast('Added to pipeline ✓')
      void refreshCounts()
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Someone else pursued it since this page loaded — reflect reality, don't error.
        setPipelineOppIds((prev) => new Set(prev).add(opp.id))
        toast('Already in pipeline')
      } else if (!(e instanceof UnauthorizedError)) {
        toast(e instanceof Error ? e.message : 'Could not add to pipeline.')
      }
    }
  }

  async function deleteOpp(opp: Opportunity) {
    // window.confirm over a custom modal: destructive, rare, admin-only — not worth a component.
    // The cascade warning matters: the DB removes any pipeline entry with it (ON DELETE CASCADE).
    if (!window.confirm(`Delete "${opp.title}"?\nThis also removes it from the pipeline.`)) return
    try {
      await api.deleteOpportunity(opp.id)
      setOpps((prev) => prev.filter((o) => o.id !== opp.id))
      setPipelineOppIds((prev) => {
        const next = new Set(prev)
        next.delete(opp.id)
        return next
      })
      toast('Opportunity deleted')
      void refreshCounts()
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // Another admin beat us to it — reflect reality, don't error.
        setOpps((prev) => prev.filter((o) => o.id !== opp.id))
        toast('Already deleted')
        void refreshCounts()
      } else if (!(e instanceof UnauthorizedError)) {
        toast(e instanceof Error ? e.message : 'Could not delete the opportunity.')
      }
    }
  }

  async function copyCsv() {
    try {
      const count = await exportCsv(opps)
      toast(`Copied ${count} opportunities — paste into Excel ✓`)
    } catch {
      toast('Copy failed — your browser blocked clipboard access')
    }
  }

  const canPursue = can('RESEARCHER')
  const canDelete = can('ADMIN')

  const card = (n: Opportunity) => (
    <OppCard
      key={n.id}
      opp={n}
      open={openCards.has(n.id)}
      inPipeline={pipelineOppIds.has(n.id)}
      canPursue={canPursue}
      canDelete={canDelete}
      onToggle={() => toggleCard(n.id)}
      onPursue={() => void pursue(n)}
      onDelete={() => void deleteOpp(n)}
    />
  )

  return (
    <div className="view active">
      <div className="dash-header dash-row">
        <div>
          <h1 className="dash-title">Opportunity Intelligence</h1>
          <div className="dash-sub">
            {loading
              ? 'Loading opportunities...'
              : opps.length
                ? `${hot.length} to pursue · ${warm.length} to watch · last updated ${formatDate(lastUpdated)}`
                : 'No opportunities loaded yet — paste a CA email to scan'}
          </div>
        </div>
        <div className="dash-actions">
          <button
            className="btn-outline"
            onClick={() => void copyCsv()}
            title="Copy HOT + WARM as CSV for Excel"
          >
            📋 Copy CSV
          </button>
          <button
            className="btn-report"
            onClick={() => {
              generateReport(opps)
              toast('Report downloaded — open in browser and Ctrl+P to print or save as PDF')
            }}
          >
            📄 Board Report
          </button>
        </div>
      </div>

      <div className="stat-row">
        <div className={`stat-card urgent${(scanMode ? legacyScan && legacyBand === 'urgent' : filter === 'urgent') ? ' active' : ''}`} onClick={() => clearScan('urgent')}>
          <div className="stat-val">{urgent.length}</div>
          <div className="stat-lbl">⚡ Act this week</div>
        </div>
        <div className={`stat-card hot${(scanMode ? legacyScan && legacyBand === 'hot' : filter === 'hot') ? ' active' : ''}`} onClick={() => clearScan('hot')}>
          <div className="stat-val">{hot.length}</div>
          <div className="stat-lbl">HOT — pursue</div>
        </div>
        <div className={`stat-card warm${(scanMode ? legacyScan && legacyBand === 'warm' : filter === 'warm') ? ' active' : ''}`} onClick={() => clearScan('warm')}>
          <div className="stat-val">{warm.length}</div>
          <div className="stat-lbl">WARM — watch</div>
        </div>
        <div className={`stat-card total${(scanMode ? legacyScan && legacyBand === 'all' : filter === 'all') ? ' active' : ''}`} onClick={() => clearScan('all')}>
          <div className="stat-val">{opps.length}</div>
          <div className="stat-lbl">Total scored</div>
        </div>
      </div>

      {scanMode && (
        <div className="scan-subset-note" role="status">
          {legacyScan
            ? `This scan predates per-scan links, so its exact finds are not recorded — showing ${
                legacyBand === 'all' ? 'the whole board' : `all ${legacyBand.toUpperCase()}`
              } instead.`
            : `Showing only the ${scanOpps.length} ${scanOpps.length === 1 ? 'opportunity' : 'opportunities'} saved by this scan.`}{' '}
          <button onClick={() => clearScan('all')}>Show all</button>
        </div>
      )}

      {error && (
        <div className="err" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="empty">
          <div className="empty-icon">🔍</div>
          <div className="empty-title">No opportunities here</div>
          <div className="empty-sub">Try a different filter or scan a new batch</div>
        </div>
      )}

      {filtered.length > 0 && (
        <div>
          {filter === 'all' && (
            <div className="section-title">
              Top opportunities right now <span />
            </div>
          )}
          {top10.map(card)}
          {rest.length > 0 && (
            <>
              <div className="section-title" style={{ marginTop: 20 }}>
                All others ({rest.length}) <span />
              </div>
              {rest.map(card)}
            </>
          )}
        </div>
      )}
    </div>
  )
}
